import { createContext, useContext, useState } from "react";

const CampaignContext = createContext(undefined);

// Mock campaigns data
const mockCampaigns = [
  {
    id: "1",
    name: "Q3 Sales Outreach",
    status: "active",
    recipients: 45,
    opens: 32,
    replies: 8,
    createdAt: new Date().toISOString()
  },
  {
    id: "2",
    name: "Product Launch Announcement",
    status: "completed",
    recipients: 120,
    opens: 98,
    replies: 23,
    createdAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString()
  }
];

export function CampaignProvider({ children }) {
  const [campaigns, setCampaigns] = useState(mockCampaigns);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEmailConnected, setIsEmailConnected] = useState(false);
  const [selectedRecipients, setSelectedRecipientsState] = useState({
    people: [
      {
        id: "1",
        name: "John Smith",
        title: "Chief Technology Officer",
        company: "Acme Corp",
        email: "john.smith@acmecorp.com",
        location: "San Francisco, CA"
      },
      {
        id: "2",
        name: "Jane Doe",
        title: "Marketing Director",
        company: "TechGiant",
        email: "jane.doe@techgiant.com",
        location: "New York, NY"
      },
      {
        id: "3",
        name: "Michael Johnson",
        title: "Chief Executive Officer",
        company: "InnovateTech",
        email: "m.johnson@innovatetech.com",
        location: "Austin, TX"
      }
    ],
    companies: [
      {
        id: "1",
        name: "Acme Corp",
        industry: "Technology",
        location: "San Francisco, CA",
        size: "500-1000",
        website: "acmecorp.com"
      },
      {
        id: "2",
        name: "TechGiant",
        industry: "Software",
        location: "New York, NY",
        size: "1000+",
        website: "techgiant.com"
      }
    ]
  });

  const setSelectedRecipients = (people, companies) => {
    setSelectedRecipientsState({ people, companies });
  };

  const clearSelectedRecipients = () => {
    setSelectedRecipientsState({ people: [], companies: [] });
  };

  const createCampaign = async (
    name,
    recipientIds,
    templateId,
    customMessage
  ) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const newCampaign = {
        id: Date.now().toString(),
        name,
        status: "active",
        recipients: recipientIds.length,
        opens: 0,
        replies: 0,
        createdAt: new Date().toISOString()
      };
      
      setCampaigns(prev => [newCampaign, ...prev]);
      clearSelectedRecipients();
    } catch (err) {
      setError("Failed to create campaign");
    } finally {
      setIsLoading(false);
    }
  };

  const connectEmailProvider = async (provider) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Simulate OAuth flow
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // For demo purposes, randomly succeed or fail
      const success = Math.random() > 0.3;
      
      if (success) {
        setIsEmailConnected(true);
        return true;
      } else {
        throw new Error(`Failed to connect to ${provider}`);
      }
    } catch (err) {
      setError(err.message || "Failed to connect email provider");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    campaigns,
    isLoading,
    error,
    selectedRecipients,
    setSelectedRecipients,
    clearSelectedRecipients,
    createCampaign,
    connectEmailProvider,
    isEmailConnected
  };

  return (
    <CampaignContext.Provider value={value}>
      {children}
    </CampaignContext.Provider>
  );
}

export function useCampaign() {
  const context = useContext(CampaignContext);
  if (context === undefined) {
    throw new Error("useCampaign must be used within a CampaignProvider");
  }
  return context;
}